import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

// get quotations
export const getQuotations = createAsyncThunk(
  "quotations/getQuotations",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (params.project_id)
        queryParams.append("project_id", params.project_id);
      if (params.client_label)
        queryParams.append("client_label", params.client_label);
      if (params.customer_id)
        queryParams.append("customer_id", params.customer_id);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_order)
        queryParams.append("sort_order", params.sort_order);
      const response = await axios.get(
        `${BASE_URL}/quotations?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get quotations!";
      return rejectWithValue(message);
    }
  },
);

// add quotation
export const addQuotation = createAsyncThunk(
  "quotations/addQuotation",
  async (quotationData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(
        `${BASE_URL}/quotations`,
        quotationData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      let message = error.response?.data?.message || "Failed to add quotation!";
      if (error.response?.data?.errors) {
        message = Object.values(error.response.data.errors).flat().join(", ");
      }
      return rejectWithValue(message);
    }
  },
);

// update quotation
export const updateQuotation = createAsyncThunk(
  "quotations/updateQuotation",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.put(`${BASE_URL}/quotations/${id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      let message =
        error.response?.data?.message || "Failed to update quotation!";
      if (error.response?.data?.errors) {
        message = Object.values(error.response.data.errors).flat().join(", ");
      }
      return rejectWithValue(message);
    }
  },
);

// get quotation details
export const getQuotationDetails = createAsyncThunk(
  "quotations/getQuotationDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(`${BASE_URL}/quotations/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get quotation details!";
      return rejectWithValue(message);
    }
  },
);

// send quotation to client
export const sendQuotationToClient = createAsyncThunk(
  "quotations/sendQuotationToClient",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(
        `${BASE_URL}/quotations/${id}/send-to-client`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to send quotation to client!";
      return rejectWithValue(message);
    }
  },
);

// approve quotation items
export const approveQuotationItems = createAsyncThunk(
  "quotations/approveQuotationItems",
  async ({ id, item_ids }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(
        `${BASE_URL}/quotations/${id}/approve-items`,
        { item_ids },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to approve quotation items!";
      return rejectWithValue(message);
    }
  },
);

// delete quotation
export const deleteQuotation = createAsyncThunk(
  "quotations/deleteQuotation",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.delete(`${BASE_URL}/quotations/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete quotation!";
      return rejectWithValue(message);
    }
  },
);
